import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCrmData } from '../contexts/CrmDataContext';
import {
  ensureWebPushSubscription,
  isWebPushConfigured,
  registerServiceWorker,
} from '../lib/webPush';
import { playNotificationSound } from '../lib/notificationSound';
import { requestNotificationPermission, showBrowserNotification } from '../lib/browserNotifications';
import {
  dismissNotificationPrompt,
  getNotificationSetupState,
  isNotificationPromptDismissed,
  type NotificationSetupState,
} from '../lib/notificationSetup';
import type { CrmNotification } from '../types/crm';
import { resolveNotificationLink } from '../lib/reminderNotifications';
import { usePortal } from '../contexts/PortalContext';
import { isSoftPortalSwitchInGrace } from '../lib/portal';

const POLL_MS = 60_000;

export function useNotificationAlerts() {
  const { profile } = useAuth();
  const { basePath } = usePortal();
  const { notifications, refresh, markNotificationRead } = useCrmData();
  const navigate = useNavigate();
  const seenIdsRef = useRef<Set<string>>(new Set());
  const bootstrappedRef = useRef(false);
  const [pendingToasts, setPendingToasts] = useState<CrmNotification[]>([]);
  const [setupState, setSetupState] = useState<NotificationSetupState>(getNotificationSetupState);
  const [promptDismissed, setPromptDismissed] = useState(isNotificationPromptDismissed);

  const clearPendingToasts = useCallback(() => setPendingToasts([]), []);

  const dismissPermissionPrompt = useCallback(() => {
    dismissNotificationPrompt();
    setPromptDismissed(true);
  }, []);

  const openAlert = useCallback(
    async (notification: CrmNotification) => {
      if (!notification.read_at) {
        await markNotificationRead(notification.id);
      }
      if (notification.link) {
        const target = resolveNotificationLink(notification.link, basePath);
        const hashIndex = target.indexOf('#');
        if (hashIndex >= 0) {
          navigate({ pathname: target.slice(0, hashIndex), hash: target.slice(hashIndex) });
        } else {
          navigate(target);
        }
      }
    },
    [markNotificationRead, navigate, basePath],
  );

  const enableNotifications = useCallback(async () => {
    const next = await requestNotificationPermission();
    const state = getNotificationSetupState();
    setSetupState(state);

    if (next === 'granted' && profile?.id) {
      setPromptDismissed(true);
      await registerServiceWorker();
      await ensureWebPushSubscription(profile.id);
    } else if (state === 'ios-browser') {
      setPromptDismissed(false);
    }
  }, [profile?.id]);

  const showPermissionPrompt = !promptDismissed && setupState !== 'granted';

  useEffect(() => {
    const sync = () => setSetupState(getNotificationSetupState());
    sync();
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  useEffect(() => {
    if (!profile?.id) {
      bootstrappedRef.current = false;
      seenIdsRef.current = new Set();
      setPendingToasts([]);
      return;
    }

    if (!bootstrappedRef.current) {
      notifications.forEach((n) => seenIdsRef.current.add(n.id));
      bootstrappedRef.current = true;

      // Once per tab session — remounting CRM/IT AppShell must not re-toast unread.
      let alreadyBootToasted = false;
      try {
        const bootKey = `bth_notif_boot_toast_${profile.id}`;
        alreadyBootToasted = sessionStorage.getItem(bootKey) === '1';
        if (!alreadyBootToasted) sessionStorage.setItem(bootKey, '1');
      } catch {
        alreadyBootToasted = false;
      }

      const unread = notifications.filter((n) => !n.read_at);
      if (!alreadyBootToasted && unread.length > 0) {
        const first = unread[0]!;
        const bootAlerts =
          unread.length === 1
            ? [first]
            : [
                {
                  ...first,
                  title: `${unread.length} unread notifications`,
                  body: 'Open Notifications to review them.',
                  kind: 'unread_summary',
                },
              ];
        setPendingToasts(bootAlerts);

        const useClientBrowserNotification =
          setupState === 'granted' && !isWebPushConfigured();
        if (useClientBrowserNotification) {
          showBrowserNotification({
            id: first.id,
            title:
              unread.length === 1 ? first.title : `${unread.length} unread notifications`,
            body:
              unread.length === 1
                ? (first.body ?? '')
                : 'Open Notifications to review them.',
            link: first.link,
          });
        }
      }
      return;
    }

    const freshUnread = notifications.filter((n) => !n.read_at && !seenIdsRef.current.has(n.id));
    if (freshUnread.length === 0) return;

    freshUnread.forEach((n) => seenIdsRef.current.add(n.id));
    playNotificationSound();
    setPendingToasts(freshUnread);

    const useClientBrowserNotification = setupState === 'granted' && !isWebPushConfigured();
    if (useClientBrowserNotification) {
      for (const n of freshUnread) {
        showBrowserNotification(n);
      }
    }
  }, [notifications, profile?.id, setupState]);

  useEffect(() => {
    if (!profile?.id) return;
    const id = window.setInterval(
      () => void refresh({ silent: true, slices: ['notifications'] }),
      POLL_MS,
    );
    return () => window.clearInterval(id);
  }, [profile?.id, refresh]);

  useEffect(() => {
    if (!profile?.id || setupState !== 'granted') return;
    // After choose-portal, wait out SW grace so update/reload cannot flash Verifying twice.
    if (isSoftPortalSwitchInGrace()) {
      const timer = window.setInterval(() => {
        if (isSoftPortalSwitchInGrace()) return;
        window.clearInterval(timer);
        void ensureWebPushSubscription(profile.id);
      }, 400);
      return () => window.clearInterval(timer);
    }
    void ensureWebPushSubscription(profile.id);
  }, [profile?.id, setupState]);

  return {
    pendingToasts,
    clearPendingToasts,
    openAlert,
    setupState,
    showPermissionPrompt,
    enableNotifications,
    dismissPermissionPrompt,
    webPushConfigured: isWebPushConfigured(),
  };
}
